/**
 * Onboarding a client, on one form.
 *
 * ## Why this is not `EntityDialog`
 *
 * `EntityDialog` edits one node of the tree, and is data-driven from `SPECS`
 * per level. This is a different job: it takes four levels at once, because
 * `client > application > project > feature > task` has no optional levels and
 * a new client therefore needed four creates before a single task could exist.
 * `docs/FINDINGS.md` F8.
 *
 * Bending the generic dialog into taking four levels would have made every
 * other use of it carry the shape of this one.
 *
 * ## Nothing is invented
 *
 * Every level is a field the person fills in. No "General" application is
 * defaulted into existence behind their back: ADR-0003 says spend and ROI roll
 * up to the application, so a silently-created one would appear in that
 * client's own dashboard under a name nobody chose. The hierarchy is not the
 * problem; four round trips to satisfy it was.
 *
 * The server writes all four in one transaction, so a failure part-way leaves
 * nothing rather than a client with no application.
 */
import type { FormEvent } from 'react';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setUpClientSchema } from '@botguide/shared';

import { Button, Dialog, ErrorNote, Field } from '@/components';
import { clientsApi } from '@/lib/api';
import { queryKeys } from '@/lib/queries';

const FORM_ID = 'set-up-client';

/** `slugSchema` shape, derived so nobody has to think about it. */
function slugFrom(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function SetUpClientDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [applicationName, setApplicationName] = useState('');
  const [projectName, setProjectName] = useState('');
  const [featureName, setFeatureName] = useState('General');
  const [issues, setIssues] = useState<Record<string, string>>({});

  const create = useMutation({
    mutationFn: clientsApi.setUp,
    onSuccess: () => {
      /*
       * The same caches a tree write invalidates. A new client changes the work
       * tree, every client dropdown on five screens, and the board's filters.
       */
      for (const key of [
        queryKeys.clients.all,
        queryKeys.applications.all,
        queryKeys.projects.all,
        queryKeys.features.all,
        queryKeys.tasks.all,
        queryKeys.board.all,
      ]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();

    const parsed = setUpClientSchema.safeParse({
      name,
      slug,
      applicationName,
      projectName,
      featureName,
    });

    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? '');
        next[key] ??= issue.message;
      }
      setIssues(next);
      return;
    }

    setIssues({});
    create.mutate(parsed.data);
  }

  const clear = (key: string) =>
    setIssues((current) => {
      const { [key]: _removed, ...rest } = current;
      return rest;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title="Set up a client"
      description="A client needs an application, a project and a feature before any task can be filed against it. Name them here and it is done in one go."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form={FORM_ID} loading={create.isPending}>
            Create
          </Button>
        </>
      }
    >
      <form id={FORM_ID} className="flex flex-col gap-4" onSubmit={submit}>
        {create.error ? <ErrorNote error={create.error} /> : null}

        <Field
          label="Client name"
          required
          autoFocus
          value={name}
          error={issues.name ?? null}
          onChange={(event) => {
            setName(event.target.value);
            // The slug follows the name until somebody takes it over, so the
            // common case is one field rather than two.
            if (!slugEdited) setSlug(slugFrom(event.target.value));
            clear('name');
            clear('slug');
          }}
        />

        <Field
          label="Slug"
          required
          hint="Lowercase, digits and hyphens. Identity — renaming the client later will not change it."
          value={slug}
          error={issues.slug ?? null}
          onChange={(event) => {
            setSlugEdited(true);
            setSlug(event.target.value);
            clear('slug');
          }}
        />

        <Field
          label="First application"
          required
          hint="What value rolls up to — spend and projected return are reported against this."
          value={applicationName}
          error={issues.applicationName ?? null}
          onChange={(event) => {
            setApplicationName(event.target.value);
            clear('applicationName');
          }}
        />

        <Field
          label="First project"
          required
          hint="A month's engagement, typically."
          value={projectName}
          error={issues.projectName ?? null}
          onChange={(event) => {
            setProjectName(event.target.value);
            clear('projectName');
          }}
        />

        <Field
          label="First feature"
          required
          hint="Tasks hang off a feature. Rename it later; this is somewhere to start."
          value={featureName}
          error={issues.featureName ?? null}
          onChange={(event) => {
            setFeatureName(event.target.value);
            clear('featureName');
          }}
        />
      </form>
    </Dialog>
  );
}
